/**
 * useSparklinePath — memoized polyline points for a tiny sparkline.
 *
 * @param {number[]} values
 * @param {{ w?: number, h?: number, pad?: number }} options
 * @returns {{ points: string, last: {x:number,y:number}|null, max: number, w: number, h: number }}
 */

import { useMemo } from "react";

export default function useSparklinePath(values, { w = 160, h = 40, pad = 4 } = {}) {
  return useMemo(() => {
    const list = Array.isArray(values) ? values.map((v) => Number(v) || 0) : [];
    if (list.length === 0) return { points: "", last: null, max: 0, w, h };
    const max = Math.max(1, ...list);
    const innerW = w - pad * 2;
    const innerH = h - pad * 2;
    const stepX = list.length > 1 ? innerW / (list.length - 1) : 0;
    const coords = list.map((v, i) => ({
      x: pad + i * stepX,
      y: pad + innerH - (v / max) * innerH,
    }));
    return {
      points: coords.map((c) => `${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(" "),
      last: coords[coords.length - 1],
      max,
      w,
      h,
    };
  }, [values, w, h, pad]);
}
